/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Sentient Vault — Onboarding As Conversion Journey
 * ──────────────────────────────────────────────────
 * P0 Stability Hotfix (June-2026):
 *   1. NO backdrop blur — target must stay crystal-clear
 *   2. Real cutout spotlight (infinite box-shadow) hugs the target;
 *      missing/oversized/zero-size targets fall back to centred mode
 *      and NEVER paint a giant empty rectangle
 *   3. Five-channel dismissal — X / ESC / outside-click / Skip-Tour
 *      confirm / Guide-disable. Tour cannot resurrect across reloads.
 *   4. Validated target resolution with ``validateTourTarget`` that
 *      logs ``target_resolution_failed`` to backend analytics
 *
 * This is NOT a feature tour. It is a five-step conviction journey:
 *
 *    Step 1 — Problem        ("Your Bank Balance Is Lying To You")
 *    Step 2 — Pain           ("Why Profitable Companies Run Out Of Cash")
 *    Step 3 — Understanding  ("The Financial Truth Layer" + demo)
 *    Step 4 — Protection     ("Sentinel Watches The Gaps")
 *    Step 5 — Action         ("Upload A Statement")
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  ShieldCheck,
  ScrollText,
  Bot,
  Cog,
  Upload,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCAL_CACHE_KEY = "sv_onboarding_done_v3";
// Session-storage flag prevents tour from "resurrecting" mid-session
// after the user explicitly dismissed it (X / ESC / outside-click /
// Skip-confirm). Survives until the tab is closed.
const SESSION_DISMISSED_KEY = "sv_onboarding_dismissed_v1";
const STEP_SCROLL_DELAY_MS = 500;
const STEP_TRANSITION_MS = 100;
const VISIBILITY_RETRY_LIMIT = 3;
// Targets larger than these thresholds are treated as "no useful
// spotlight" — the overlay falls back to centred mode rather than
// painting a giant orange box across half the screen.
const MAX_TARGET_HEIGHT_RATIO = 0.70;   // 70 % of viewport height
const MAX_TARGET_WIDTH_RATIO  = 0.92;   // 92 % on tablet+desktop
// Mobile-aware width ratio — at sub-480 widths a hero banner of
// course fills almost the entire viewport, so we relax the gate
// to 0.97 so it still gets a spotlight cutout rather than the
// (visually weaker) centred fallback.
function _maxWidthRatioForViewport(vw) {
  if (!Number.isFinite(vw)) return MAX_TARGET_WIDTH_RATIO;
  if (vw <= 480) return 0.97;
  if (vw <= 768) return 0.95;
  return MAX_TARGET_WIDTH_RATIO;
}
// Padding around the spotlighted element — small so it hugs.
const SPOTLIGHT_PADDING_PX = 8;

// ────────────────────────────────────────────────────────────────
// Conviction journey — copy locked by the brief.
// Each step opens with the PROBLEM and ends with the SOLUTION.
// ``trackEvent`` maps to the backend whitelist in
// ``_ALLOWED_ONBOARDING_EVENTS`` (server.py).
// ────────────────────────────────────────────────────────────────
const STEPS = [
  {
    id: "problem-bank-balance-lying",
    phase: "Problem",
    title: "Your Bank Balance Is Lying To You",
    description:
      "Most businesses look at cash. Sentient Vault looks at ownership. " +
      "Just because money sits in your account doesn't mean it belongs to you.",
    bullets: ["VAT", "PAYE", "Supplier commitments", "Customer deposits"],
    why:
      "If you cannot tell whose money is in your account, you cannot tell which pound you are allowed to spend.",
    selectors: [
      '[data-tour="reality-banner"]',
      '[data-testid="financial-reality-banner"]',
    ],
    trackEvent: "problem_step_viewed",
    scrollBehavior: "center",
    position: "bottom",
  },
  {
    id: "pain-profitable-but-broke",
    phase: "Pain",
    title: "Why Profitable Companies Run Out Of Cash",
    description:
      "Many businesses fail despite appearing profitable. They spend obligation money as though it were free money.",
    bullets: [
      "Owned money",
      "Owed money",
      "Trusted money",
      "Reserved money",
    ],
    why:
      "Profit on a P&L is not the same thing as cash you are free to spend. The gap between the two is where companies die.",
    selectors: [
      '[data-tour="ownership-breakdown"]',
      '[data-testid="ownership-section"]',
    ],
    trackEvent: "pain_step_viewed",
    scrollBehavior: "center",
    position: "bottom",
  },
  {
    id: "understanding-financial-truth",
    phase: "Understanding",
    title: "The Financial Truth Layer",
    description:
      "Traditional software shows transactions. Sentient Vault shows ownership. " +
      "The question changes from \u201Cwhat happened?\u201D to \u201Cwho does this money belong to?\u201D",
    why:
      "Once you can see ownership, every spending decision becomes a deliberate choice instead of an unconscious gamble.",
    selectors: [
      '[data-tour="vault-intelligence"]',
      '[data-testid="vault-intelligence"]',
    ],
    trackEvent: "ownership_step_viewed",
    scrollBehavior: "center",
    position: "top",
    demoScenario: true,
  },
  {
    id: "protection-sentinel-watches",
    phase: "Protection",
    title: "Sentinel Watches The Gaps",
    description:
      "Sentinel AI continuously reviews tax exposure, supplier obligations, trust liabilities and cash discipline \u2014 before they become expensive problems.",
    bullets: [
      "Tax exposure",
      "Supplier obligations",
      "Trust liabilities",
      "Cash discipline",
    ],
    why:
      "Numbers move faster than weekly reviews. A guardian that watches every transaction catches the leak you would have spotted on Friday \u2014 on Monday morning.",
    selectors: [
      '[data-tour="ai-warnings"]',
      '[data-testid="ai-warnings-section"]',
    ],
    trackEvent: "sentinel_step_viewed",
    scrollBehavior: "center",
    position: "top",
    confidenceSignals: true,
  },
  {
    id: "action-upload-statement",
    phase: "Action",
    title: "Upload A Statement",
    description:
      "Let's discover how much money truly belongs to your business. Drop in one bank statement and watch Financial Truth appear.",
    why:
      "The fastest route from \u201CI think my numbers are fine\u201D to \u201CI know which pounds I can spend\u201D is one upload away.",
    selectors: [
      '[data-tour="action-layer"]',
      '[data-testid="action-layer"]',
    ],
    trackEvent: "upload_cta_clicked",
    scrollBehavior: "center",
    position: "top",
    primaryCTA: { label: "Open Document Intake", testid: "onboarding-upload-cta" },
  },
];

// Confidence signals shown inside the Protection step. Pulled from
// the brief verbatim so the user is reassured Sentient Vault is not
// just another dashboard.
const CONFIDENCE_SIGNALS = [
  { icon: ShieldCheck, label: "Ledger Protected" },
  { icon: ScrollText, label: "Audit Trail Active" },
  { icon: Bot, label: "AI Advisory Only" },
  { icon: Cog, label: "Deterministic Allocation Engine Active" },
];

// Demo scenario shown inside Step 3 (Understanding). This is the
// "First Truth Realisation" moment — the highest-converting beat
// of the journey.
const DEMO_SCENARIO = {
  bankBalance: "\u00A3250,000",
  freeToSpend: "\u00A374,000",
  breakdown: [
    { label: "Supplier obligations", value: "\u00A381,000" },
    { label: "Tax custody",          value: "\u00A343,000" },
    { label: "Customer trust",       value: "\u00A352,000" },
  ],
};

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────
function resolveStepEl(step) {
  for (const sel of step.selectors) {
    try {
      const el = document.querySelector(sel);
      if (el) return el;
    } catch (_) { /* ignore bad selectors */ }
  }
  return null;
}

/** Diagnostic validation of a spotlight target.
 *
 *  Returns an object the caller can inspect to decide whether to
 *  render the spotlight in cutout mode (target exists + visible +
 *  reasonable size) or fall back to centred mode (anything else).
 *
 *  Why ``oversized`` matters: a ``<section>`` that wraps the entire
 *  dashboard would otherwise produce a giant orange rectangle —
 *  exactly the bug reported in the field.
 */
function validateTourTarget(el) {
  if (!el || !el.getBoundingClientRect) {
    return { exists: false, mounted: false, visible: false,
             width: 0, height: 0, oversized: false, reason: "no_element" };
  }
  const rect = el.getBoundingClientRect();
  const width = rect.width;
  const height = rect.height;
  const vw = window.innerWidth || document.documentElement.clientWidth || 1;
  const vh = window.innerHeight || document.documentElement.clientHeight || 1;
  if (width < 4 || height < 4) {
    return { exists: true, mounted: true, visible: false,
             width, height, oversized: false, reason: "zero_size" };
  }
  // Off-screen test — must be at least PARTIALLY in viewport.
  const visibleW = Math.max(0, Math.min(rect.right, vw) - Math.max(rect.left, 0));
  const visibleH = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
  const visible = visibleW > 0 && visibleH > 0;
  if (!visible) {
    return { exists: true, mounted: true, visible: false,
             width, height, oversized: false, reason: "off_screen" };
  }
  const oversized = height / vh > MAX_TARGET_HEIGHT_RATIO
                 || width  / vw > _maxWidthRatioForViewport(vw);
  if (oversized) {
    return { exists: true, mounted: true, visible: true,
             width, height, oversized: true, reason: "oversized" };
  }
  // At least 60 % of the target's own area must be inside the viewport
  // — covers the case where a small element scrolled mostly out.
  const visibleArea = visibleW * visibleH;
  const totalArea = width * height;
  const enoughInside = totalArea > 0 && (visibleArea / totalArea) >= 0.6;
  if (!enoughInside) {
    return { exists: true, mounted: true, visible: false,
             width, height, oversized: false, reason: "scrolled_off" };
  }
  return { exists: true, mounted: true, visible: true,
           width, height, oversized: false, reason: "ok" };
}

function readRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 4 || r.height < 4) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** Fire-and-forget analytics. The endpoint is whitelist-guarded
 *  server-side so an unknown name returns 422 — caught + swallowed
 *  here so the UI never blocks. */
function track(token, event, payload = {}) {
  if (!token || !event) return;
  try {
    axios.post(`${API}/onboarding/event`, { event, ...payload }, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* swallowed */ });
  } catch (_) { /* swallowed */ }
}

// ── Dismissal persistence helpers ───────────────────────────────
// Three layers of protection prevent the tour from "resurrecting":
//   1. localStorage  — survives across tabs and reloads
//   2. sessionStorage — survives across reloads in the same tab,
//                       lets the parent ``useEffect`` block re-open
//   3. backend POST   — authoritative across re-logins
function persistDismissal() {
  try { localStorage.setItem(LOCAL_CACHE_KEY, String(Date.now())); } catch (_) { /* */ }
  try { sessionStorage.setItem(SESSION_DISMISSED_KEY, "1"); } catch (_) { /* */ }
}
function isSessionDismissed() {
  try { return sessionStorage.getItem(SESSION_DISMISSED_KEY) === "1"; }
  catch (_) { return false; }
}
function clearSessionDismissal() {
  try { sessionStorage.removeItem(SESSION_DISMISSED_KEY); } catch (_) { /* */ }
}

// ────────────────────────────────────────────────────────────────
// Public hook — drives backend state + restart
// ────────────────────────────────────────────────────────────────
export function useOnboardingState({ token, user, paused = false } = {}) {
  const [state, setState] = useState({
    loading: true,
    shouldShow: false,
    completedAt: null,
    skippedAt: null,
    version: 1,
  });
  const fetchedRef = useRef(false);

  const fetchState = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/onboarding/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // ── P0 Hotfix: a tour the user dismissed THIS session must
      // never resurrect, even if the backend hasn't yet recorded
      // ``completed_at`` (request in-flight / network blip).
      const sessionDismissed = isSessionDismissed();
      setState({
        loading: false,
        shouldShow: !!data.should_show && !sessionDismissed,
        completedAt: data.completed_at,
        skippedAt: data.skipped_at,
        version: data.version,
      });
      try {
        if (data.should_show && !sessionDismissed) localStorage.removeItem(LOCAL_CACHE_KEY);
        else localStorage.setItem(LOCAL_CACHE_KEY, String(data.version || 1));
      } catch (_) { /* private mode etc. */ }
    } catch (_) {
      let cached = null;
      try { cached = localStorage.getItem(LOCAL_CACHE_KEY); } catch (_) { /* */ }
      const sessionDismissed = isSessionDismissed();
      setState({
        loading: false,
        shouldShow: (!cached) && !sessionDismissed,
        completedAt: null,
        skippedAt: null,
        version: 1,
      });
    }
  }, [token]);

  useEffect(() => {
    if (paused) {
      setState((s) => ({ ...s, loading: false, shouldShow: false }));
      return;
    }
    if (!token) {
      setState((s) => ({
        ...s,
        loading: false,
        shouldShow: false,
        completedAt: null,
        skippedAt: null,
        version: 1,
      }));
      return;
    }
    if (user?.role === "admin" || user?.is_admin) {
      setState((s) => ({ ...s, loading: false, shouldShow: false }));
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchState();
  }, [token, paused, user, fetchState]);

  const start = useCallback(async () => {
    if (!token) return;
    // Guide button is the ONLY restart path — explicitly clear the
    // session-dismissal flag so the hook can fire again.
    clearSessionDismissal();
    try {
      await axios.post(`${API}/onboarding/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await axios.post(`${API}/onboarding/start`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) { /* still open the tour locally */ }
    try { localStorage.removeItem(LOCAL_CACHE_KEY); } catch (_) { /* */ }
    setState((s) => ({ ...s, loading: false, shouldShow: true, completedAt: null, skippedAt: null }));
  }, [token]);

  const close = useCallback(({ skipped = false, lastStep = 0 } = {}) => {
    // Persist dismissal across all three layers immediately —
    // even before the backend roundtrip completes the local state
    // already blocks any re-render from re-opening the tour.
    persistDismissal();
    setState((s) => ({ ...s, shouldShow: false }));
    if (!token) return;
    axios.post(`${API}/onboarding/complete`, { skipped, last_step: lastStep }, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* swallowed — local cache covers it */ });
  }, [token]);

  return { ...state, start, close, refetch: fetchState };
}


// ────────────────────────────────────────────────────────────────
// Inline content blocks (demo scenario, confidence signals, bullets)
// Defined OUTSIDE the overlay component so they are not re-created
// on every render.
// ────────────────────────────────────────────────────────────────
function StepBullets({ bullets }) {
  if (!bullets?.length) return null;
  return (
    <ul className="mt-2 mb-3 grid grid-cols-2 gap-x-3 gap-y-1.5" data-testid="onboarding-bullets">
      {bullets.map((b) => (
        <li key={b} className="flex items-center gap-2 text-xs text-slate-200/90">
          <span className="w-1 h-1 rounded-full bg-amber-400/80" />
          {b}
        </li>
      ))}
    </ul>
  );
}

function DemoScenarioBlock() {
  return (
    <div
      className="mb-3 rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.07] to-transparent p-4"
      data-testid="onboarding-demo-scenario"
    >
      <div className="text-[9px] text-amber-400/80 font-mono uppercase tracking-wider mb-2">
        Live example — Financial Truth Revealed
      </div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs text-slate-400">Bank Balance</span>
        <span className="text-base font-semibold text-slate-300 line-through decoration-slate-500/60">
          {DEMO_SCENARIO.bankBalance}
        </span>
      </div>
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-xs text-amber-300">Actually Free To Spend</span>
        <span
          className="text-2xl font-bold text-amber-300"
          data-testid="demo-free-to-spend"
        >
          {DEMO_SCENARIO.freeToSpend}
        </span>
      </div>
      <div className="space-y-1.5 pt-2 border-t border-amber-500/15">
        {DEMO_SCENARIO.breakdown.map((row) => (
          <div key={row.label} className="flex items-center justify-between text-xs">
            <span className="text-slate-400">{row.label}</span>
            <span className="text-slate-200 font-mono">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfidenceSignalsBlock() {
  return (
    <div
      className="mt-3 mb-3 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.05] p-3"
      data-testid="onboarding-confidence-panel"
    >
      <div className="text-[9px] text-emerald-300/80 font-mono uppercase tracking-wider mb-2">
        System Status
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {CONFIDENCE_SIGNALS.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-2 text-xs text-emerald-100">
            <Icon className="w-3 h-3 text-emerald-300" />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// The overlay
// ────────────────────────────────────────────────────────────────
export default function OnboardingOverlay({
  open = true,
  onClose,
  steps = STEPS,
}) {
  const { token } = useAuth();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [activeSteps, setActiveSteps] = useState([]);
  const [stepReady, setStepReady] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);
  const startedAtRef = useRef(null);
  const stepFiredRef = useRef(new Set());
  // Pending-step cancellation: every async measurement timer + retry
  // is stored here so dismissal can call ``cancelPendingSteps()`` and
  // guarantee nothing reopens the spotlight after the user has left.
  const pendingTimersRef = useRef([]);
  const dismissedRef = useRef(false);

  const cancelPendingSteps = useCallback(() => {
    pendingTimersRef.current.forEach((id) => { try { clearTimeout(id); } catch (_) { /* */ } });
    pendingTimersRef.current = [];
  }, []);

  // ── Filter to steps whose anchor exists. Steps whose anchor
  // disappeared get rendered centred (no spotlight) rather than
  // dropped — the brief insists no message in the journey is lost.
  useEffect(() => {
    if (!open) return;
    setActiveSteps(steps);
  }, [open, steps]);

  // ── P0 Stability Hotfix: every transition of ``open`` from
  // ``false`` to ``true`` (e.g. Guide button restart) must reset
  // the dismissed flag, the started-at clock, and the step pointer.
  // Without this reset the component instance carries dismissedRef
  // forward forever and the second tour is a silent no-op.
  useEffect(() => {
    if (!open) return;
    dismissedRef.current = false;
    startedAtRef.current = null;
    stepFiredRef.current = new Set();
    setStep(0);
    setConfirmSkip(false);
    cancelPendingSteps();
  }, [open, cancelPendingSteps]);

  // ── Stamp started_at + tour_started analytics
  useEffect(() => {
    if (!open || !token || startedAtRef.current) return;
    startedAtRef.current = Date.now();
    axios.post(`${API}/onboarding/start`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    track(token, "tour_started", { total_steps: steps.length });
  }, [open, token, steps.length]);

  // ── Visibility-guarded measure: scroll → wait → re-measure.
  // ``validateTourTarget`` decides whether to spotlight or fall back
  // to centred mode (missing / hidden / zero-size / OVERSIZED target).
  useEffect(() => {
    if (!open || activeSteps.length === 0) return;
    setStepReady(false);
    const currentStep = activeSteps[step];
    if (!currentStep) return;

    const el = resolveStepEl(currentStep);
    const initialValidation = validateTourTarget(el);
    if (!initialValidation.visible || initialValidation.oversized) {
      // Anchor is missing / hidden / oversized — render centred
      // tooltip with NO spotlight. Log the failure so we can fix
      // dashboard refactors that break the tour.
      if (initialValidation.reason !== "ok") {
        track(token, "target_resolution_failed", {
          step_id: currentStep.id,
          step_index: step,
          reason: initialValidation.reason,
          width: Math.round(initialValidation.width),
          height: Math.round(initialValidation.height),
        });
      }
      setRect(null);
      setStepReady(true);
      return;
    }

    let cancelled = false;
    let retries = 0;

    const tryMeasure = () => {
      if (cancelled || dismissedRef.current) return;
      try {
        el.scrollIntoView({
          behavior: "smooth",
          block: currentStep.scrollBehavior || "center",
          inline: "center",
        });
      } catch (_) { /* */ }
      const tid = setTimeout(() => {
        if (cancelled || dismissedRef.current) return;
        const v = validateTourTarget(el);
        if ((!v.visible || v.oversized) && retries < VISIBILITY_RETRY_LIMIT) {
          retries += 1;
          tryMeasure();
          return;
        }
        if (!v.visible || v.oversized) {
          // Final fallback after retries — render centred.
          if (v.reason !== "ok") {
            track(token, "target_resolution_failed", {
              step_id: currentStep.id,
              step_index: step,
              reason: v.reason,
              retries,
            });
          }
          setRect(null);
          setStepReady(true);
          return;
        }
        setRect(readRect(el));
        setStepReady(true);
      }, STEP_SCROLL_DELAY_MS);
      pendingTimersRef.current.push(tid);
    };
    tryMeasure();

    const remeasure = () => {
      if (cancelled || dismissedRef.current) return;
      const v = validateTourTarget(el);
      if (!v.visible || v.oversized) {
        setRect(null);
        return;
      }
      setRect(readRect(el));
    };
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, { passive: true });
    return () => {
      cancelled = true;
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure);
    };
  }, [open, step, activeSteps, token]);

  // ── Per-step analytics — fire phase-named conversion events.
  useEffect(() => {
    if (!open || !token || !stepReady) return;
    const currentStep = activeSteps[step];
    if (!currentStep) return;
    const key = `${currentStep.id}-${step}`;
    if (stepFiredRef.current.has(key)) return;
    stepFiredRef.current.add(key);

    // Per-phase named event (problem_step_viewed, pain_step_viewed, …)
    if (currentStep.trackEvent) {
      track(token, currentStep.trackEvent, {
        step_id: currentStep.id,
        step_index: step,
      });
    }
    // Companion generic event so legacy dashboards still get a
    // per-step counter.
    track(token, `tour_step_${step + 1}`, {
      step_id: currentStep.id,
      step_index: step,
    });
    // Side-effects for the special inline blocks.
    if (currentStep.demoScenario) {
      track(token, "demo_scenario_viewed", { step_id: currentStep.id });
    }
    if (currentStep.confidenceSignals) {
      track(token, "confidence_panel_viewed", { step_id: currentStep.id });
    }
  }, [stepReady, step, activeSteps, open, token]);

  // ─── Dismissal handlers (P0 Stability Hotfix) ─────────────
  // Five distinct channels, each fully terminating:
  //   * X button         → immediate close (dismissByX)
  //   * ESC key          → immediate close (dismissByEsc)
  //   * Outside click    → immediate close (dismissByOutsideClick)
  //   * Skip-Tour text   → confirmation, then close (confirmSkipNow)
  //   * Guide-disable    → caller closes the parent's open=false
  //
  // Each channel runs ``terminateTour(reason)`` which:
  //   1. Marks dismissedRef so in-flight measures are no-op'd
  //   2. cancelPendingSteps() clears scroll/measure timeouts
  //   3. closes the skip dialog if open
  //   4. fires the matching analytics event
  //   5. calls onClose(payload) → parent persists dismissal across
  //      localStorage + sessionStorage + backend
  //
  // Hooks MUST be declared before the early-return below so React
  // gets a stable hook-call order on every render.
  const terminateTour = useCallback((reason) => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    cancelPendingSteps();
    setConfirmSkip(false);
    const duration = startedAtRef.current ? Date.now() - startedAtRef.current : null;
    track(token, reason, {
      drop_off_step: step,
      duration_ms: duration,
    });
    track(token, "tour_skipped", {
      drop_off_step: step,
      duration_ms: duration,
      via: reason,
    });
    onClose?.({ skipped: true, lastStep: step });
  }, [token, step, onClose, cancelPendingSteps]);

  const dismissByX = useCallback(() => terminateTour("tour_dismissed_x"),
                                  [terminateTour]);
  const dismissByOutsideClick = useCallback(() => terminateTour("tour_dismissed_outside_click"),
                                              [terminateTour]);
  const dismissByEsc = useCallback(() => terminateTour("tour_dismissed_esc"),
                                    [terminateTour]);

  // Global ESC key handler — runs even when the tooltip lacks focus.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (ev) => {
      if (ev.key === "Escape" || ev.keyCode === 27) {
        ev.preventDefault();
        dismissByEsc();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, dismissByEsc]);

  if (!open || activeSteps.length === 0) return null;

  const currentStep = activeSteps[step];
  const total = activeSteps.length;
  const tooltipStyle = {
    position: "fixed",
    width: "min(440px, calc(100vw - 32px))",
    zIndex: 102,
    transition: `top ${STEP_TRANSITION_MS}ms ease, left ${STEP_TRANSITION_MS}ms ease`,
  };
  const TOOLTIP_W = 440;
  // Conservative height estimates — every step has bullets or a
  // demo/confidence block so 360px was too low (mobile clipped 48px
  // on iPhone-size viewports).
  const TOOLTIP_H = currentStep.demoScenario ? 540
                   : currentStep.confidenceSignals ? 460
                   : currentStep.bullets?.length ? 440
                   : 360;
  const margin = 16;
  // Mobile / short-viewport: the tooltip simply does not fit beside
  // the spotlight. Render centred and skip the rect-relative layout
  // so we never clip the message off-screen. The spotlight still
  // shows because ``rect`` is preserved upstream.
  const VIEWPORT_TOO_SHORT = window.innerHeight < TOOLTIP_H + 120;
  if (rect && !VIEWPORT_TOO_SHORT) {
    let top, left;
    if (currentStep.position === "bottom") {
      top = rect.top + rect.height + 14;
      left = rect.left;
    } else if (currentStep.position === "top") {
      top = rect.top - TOOLTIP_H - 14;
      left = rect.left;
    } else {
      top = rect.top;
      left = rect.left + rect.width + 14;
    }
    // Clamp vertically so the tooltip can never exceed the viewport,
    // even if it has to overlap the spotlight (the tooltip has its
    // own opaque background + a higher z-index than the spotlight).
    if (top + TOOLTIP_H > window.innerHeight - margin) {
      const above = rect.top - TOOLTIP_H - 14;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - TOOLTIP_H - margin);
    }
    if (top < margin) top = margin;
    if (left + TOOLTIP_W > window.innerWidth - margin) {
      left = window.innerWidth - TOOLTIP_W - margin;
    }
    if (left < margin) left = margin;
    tooltipStyle.top = top;
    tooltipStyle.left = left;
  } else {
    // Short viewport OR missing anchor → centred placement.
    tooltipStyle.top = "50%";
    tooltipStyle.left = "50%";
    tooltipStyle.transform = "translate(-50%, -50%)";
  }

  const handleNext = () => {
    if (step < total - 1) {
      setStep(step + 1);
    } else {
      // Reaching the end legitimately — NOT a dismissal channel,
      // but uses the same cancel-pending-steps safety net.
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      cancelPendingSteps();
      track(token, "tour_completed", {
        duration_ms: startedAtRef.current ? Date.now() - startedAtRef.current : null,
        total_steps: total,
      });
      onClose?.({ skipped: false, lastStep: total - 1 });
    }
  };
  const handleBack = () => { if (step > 0) setStep(step - 1); };

  // Skip-Tour text link still asks for confirmation — the only channel
  // that does. Confirming runs the same terminateTour pipeline.
  const requestSkip = () => setConfirmSkip(true);
  const cancelSkip = () => setConfirmSkip(false);
  const confirmSkipNow = () => terminateTour("tour_dismissed_guide_disable");

  // Final-step CTA: emit upload_cta_clicked then close + push the
  // user toward DataPool. The dashboard's "Open Document Intake"
  // button is the target.
  const handlePrimaryCTA = () => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    cancelPendingSteps();
    track(token, "upload_cta_clicked", { step_id: currentStep.id });
    track(token, "tour_completed", {
      duration_ms: startedAtRef.current ? Date.now() - startedAtRef.current : null,
      total_steps: total,
      via: "primary_cta",
    });
    onClose?.({ skipped: false, lastStep: total - 1 });
    // Soft-navigate to DataPool. Avoid hard navigate to keep SPA
    // state intact. Route registered in App.js as
    // ``/dashboard/data-pool`` (alongside legacy redirects from
    // /ingest and /data-import).
    try {
      if (window.location.pathname !== "/dashboard/data-pool") {
        window.location.assign("/dashboard/data-pool");
      }
    } catch (_) { /* */ }
  };

  const isFinalStep = step === total - 1;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="onboarding-overlay" data-onboarding-root>
      {/* Cutout spotlight uses the infinite box-shadow technique. The
          highlighted element stays CRYSTAL CLEAR; everything else is
          dimmed by the box-shadow's spread. NO blur — the target
          (and the entire dashboard underneath) remains pixel-perfect.

          When ``rect`` is null (missing / oversized / off-screen
          target) we render a plain dim backdrop instead. NEVER a
          giant orange rectangle. */}
      {rect && stepReady ? (
        <div
          className="pointer-events-none"
          style={{
            position: "fixed",
            top: Math.max(0, rect.top - SPOTLIGHT_PADDING_PX),
            left: Math.max(0, rect.left - SPOTLIGHT_PADDING_PX),
            width: rect.width + SPOTLIGHT_PADDING_PX * 2,
            height: rect.height + SPOTLIGHT_PADDING_PX * 2,
            borderRadius: 14,
            border: "2px solid rgba(255, 190, 50, 0.95)",
            // Infinite box-shadow → fills the rest of the viewport
            // with a 45 % black dim. The cutout area itself is
            // completely transparent: no blur, no filter, no opacity.
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.45)",
            background: "transparent",
            zIndex: 101,
            transition: "top 0.18s ease, left 0.18s ease, width 0.18s ease, height 0.18s ease",
          }}
          data-testid="onboarding-spotlight"
        />
      ) : (
        // No usable target → plain dim layer (no blur).
        <div
          className="absolute inset-0"
          style={{ background: "rgba(0, 0, 0, 0.45)" }}
          data-testid="onboarding-backdrop-fallback"
        />
      )}

      {/* Click-catcher that triggers dismiss-by-outside-click. Placed
          ABOVE the dim layer but BELOW the tooltip (z-101 vs z-102)
          so clicks on the spotlight cutout fall through to the
          actual product underneath — but clicks anywhere else
          dismiss the tour. */}
      <div
        className="absolute inset-0"
        style={{ background: "transparent", zIndex: 100 }}
        onClick={dismissByOutsideClick}
        data-testid="onboarding-backdrop"
      />

      {/* Tooltip card */}
      {stepReady && (
        <div
          style={{
            ...tooltipStyle,
            background: "rgba(8, 14, 32, 0.97)",
            boxShadow: "0 28px 80px rgba(0, 0, 0, 0.55)",
            maxHeight: "calc(100vh - 32px)",
            overflowY: "auto",
          }}
          className="p-6 rounded-2xl border border-amber-500/20"
          data-testid="onboarding-tooltip"
          role="dialog"
          aria-labelledby="onboarding-tooltip-title"
        >
          {/* Phase chip + step counter */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span
                className="text-[10px] text-amber-400/70 font-mono uppercase tracking-wider"
                data-testid="onboarding-step"
              >
                Step {step + 1} of {total}
              </span>
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono uppercase tracking-wider"
                data-testid="onboarding-phase"
              >
                {currentStep.phase}
              </span>
            </div>
            <button
              onClick={dismissByX}
              className="text-slate-500 hover:text-amber-400 transition-colors"
              data-testid="onboarding-skip"
              aria-label="Close onboarding"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <h3
            id="onboarding-tooltip-title"
            className="font-barlow font-bold text-xl text-slate-50 mb-2"
            data-testid="onboarding-title"
          >
            {currentStep.title}
          </h3>
          <p
            className="text-sm text-slate-200 leading-relaxed mb-1"
            data-testid="onboarding-message"
          >
            {currentStep.description}
          </p>

          {/* Bullets */}
          <StepBullets bullets={currentStep.bullets} />

          {/* Demo scenario (Step 3) */}
          {currentStep.demoScenario && <DemoScenarioBlock />}

          {/* Confidence signals (Step 4) */}
          {currentStep.confidenceSignals && <ConfidenceSignalsBlock />}

          {/* Why this matters */}
          {currentStep.why && (
            <div className="p-3 rounded-lg bg-amber-500/[0.06] border border-amber-500/15 mb-4">
              <span className="text-[9px] text-amber-400/80 font-mono uppercase tracking-wider">
                Why this matters
              </span>
              <p
                className="text-xs text-amber-200/90 mt-1 leading-relaxed"
                data-testid="onboarding-why"
              >
                {currentStep.why}
              </p>
            </div>
          )}

          {/* Phase-tracking progress bar */}
          <div className="flex gap-1.5 mb-4" data-testid="onboarding-progress">
            {activeSteps.map((s, i) => (
              <div
                key={s.id}
                className={`h-1.5 rounded-full flex-1 transition-all ${
                  i < step ? "bg-amber-400/70" :
                  i === step ? "bg-amber-400" :
                  "bg-slate-800"
                }`}
                title={s.phase}
              />
            ))}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={requestSkip}
              className="text-xs text-slate-500 hover:text-amber-400 transition-colors"
              data-testid="onboarding-skip-text"
            >
              Skip tour
            </button>
            <div className="flex gap-2">
              {step > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-slate-700 text-slate-200 hover:bg-slate-800"
                  onClick={handleBack}
                  data-testid="onboarding-back"
                >
                  <ChevronLeft className="w-3 h-3 mr-1" /> Back
                </Button>
              )}
              {isFinalStep && currentStep.primaryCTA ? (
                <Button
                  size="sm"
                  className="h-8 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                  onClick={handlePrimaryCTA}
                  data-testid={currentStep.primaryCTA.testid}
                >
                  <Upload className="w-3 h-3 mr-1" />
                  {currentStep.primaryCTA.label}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-8 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                  onClick={handleNext}
                  data-testid="onboarding-next"
                >
                  {step < total - 1 ? (
                    <>Continue <ChevronRight className="w-3 h-3 ml-1" /></>
                  ) : (
                    <span data-testid="onboarding-finish-label">
                      Enter Dashboard
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Skip confirmation */}
      <AlertDialog open={confirmSkip} onOpenChange={(v) => !v && cancelSkip()}>
        <AlertDialogContent
          className="bg-slate-900 border-slate-700"
          data-testid="onboarding-skip-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-50">
              Skip the conviction journey?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              You can restart it anytime from the Guide button at the top of the dashboard. We recommend at least seeing Step 3 — that is the moment most users say <em>“I have been managing cash, but not ownership.”</em>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-slate-700 text-slate-200 hover:bg-slate-800"
              onClick={cancelSkip}
              data-testid="onboarding-skip-cancel"
            >
              Keep watching
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-500 hover:bg-amber-400 text-slate-950"
              onClick={confirmSkipNow}
              data-testid="onboarding-skip-confirm"
            >
              Skip Tour
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
